/* eslint-disable linebreak-style */
import { Company } from 'companies/company.types';
import { ClientMigration } from 'client-migration/clientMigration.types';
import { PostMigrationNotice } from './PostMigrationNotice';
import { PreMigrationNotice } from './PreMigrationNotice';

type Platform = 'cdh' | 'cobra' | 'combined' | 'cdhTransition' | 'cobraTransition';

interface MigrationBannerProps {
  migrationStatus: ClientMigration | null;
  company: Company | null;
}

const ONE_DAY_MS = 86_400_000;

export function MigrationBanner({
  migrationStatus,
  company,
}: Readonly<MigrationBannerProps>): JSX.Element | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parseDate = (dateString?: string | null) => dateString ? new Date(dateString) : null;

  const platformMigrations = [
    {
      platformType: 'cdh' as Platform,
      plannedMigrationDate: parseDate(migrationStatus?.cdh_planned_migration_date),
      actualMigrationDate: parseDate(migrationStatus?.cdh_actual_migration_date),
      liveDate: parseDate(migrationStatus?.cdh_live_date),
      migrationWindowDays: 30,
      client_status: migrationStatus?.client_status,
    },
    {
      platformType: 'cobra' as Platform,
      plannedMigrationDate: parseDate(migrationStatus?.cobra_planned_migration_date),
      actualMigrationDate: parseDate(migrationStatus?.cobra_actual_migration_date),
      liveDate: parseDate(migrationStatus?.cobra_live_date),
      migrationWindowDays: 60,
      client_status: migrationStatus?.client_status,
    },
  ];

  const isBeforeOrOnMigrationDate = (actualDate: Date | null) => !actualDate || actualDate > today;

  const isMigrationCurrentlyActive = ({
    plannedMigrationDate,
    actualMigrationDate,
    migrationWindowDays,
  }: typeof platformMigrations[0]) => {
    if (!plannedMigrationDate) return false;

    const daysUntilPlannedMigration = (plannedMigrationDate.getTime() - today.getTime()) / ONE_DAY_MS;
    const daysSinceActualMigration = actualMigrationDate
      ? (today.getTime() - actualMigrationDate.getTime()) / ONE_DAY_MS
      : null;

    return (
      (daysUntilPlannedMigration >= 0 && daysUntilPlannedMigration <= migrationWindowDays) ||
      (daysSinceActualMigration !== null && daysSinceActualMigration >= 0)
    );
  };

  const migrationPlannedDates = {
    cdh: platformMigrations[0].plannedMigrationDate?.toDateString() ?? '',
    cobra: platformMigrations[1].plannedMigrationDate?.toDateString() ?? '',
  };

  const liveDates = {
    cdh: platformMigrations[0].liveDate?.toDateString() ?? '',
    cobra: platformMigrations[1].liveDate?.toDateString() ?? '',
  };

  if (!migrationStatus) return null;
  const activeMigrations = platformMigrations.filter(isMigrationCurrentlyActive);

  if (activeMigrations.length === 0) return null;

  if (activeMigrations.length === 2) {
    const [ cdhMigration, cobraMigration ] = platformMigrations;
    const isCdhPreMigration = isBeforeOrOnMigrationDate(cdhMigration.liveDate);
    const isCobraPreMigration = isBeforeOrOnMigrationDate(cobraMigration.liveDate);

    if (isCdhPreMigration && isCobraPreMigration) {
      return <PreMigrationNotice platform="combined" date={migrationPlannedDates} liveDate={liveDates} />;
    }

    if (!isCdhPreMigration && !isCobraPreMigration) {
      return <PostMigrationNotice platform="combined" />;
    }

    return (
      <>
        {activeMigrations.map(migration => isBeforeOrOnMigrationDate(migration.liveDate) ?
          migration.client_status === 2 || migration.client_status === 3 ? <></> : (
            <PreMigrationNotice
              key={migration.platformType}
              platform={today >= (migration.actualMigrationDate ?? today) ?
                (migration.platformType + 'Transition') as Platform : migration.platformType}
              date={migrationPlannedDates}
              liveDate={liveDates}
            />
          ) : (
            <PostMigrationNotice key={migration.platformType} platform={migration.platformType} />
          ))}
      </>
    );
  }

  const [ singleActiveMigration ] = activeMigrations;

  return isBeforeOrOnMigrationDate(singleActiveMigration.liveDate)
    ?
    <PreMigrationNotice
      platform={today >= (singleActiveMigration.actualMigrationDate ?? today) ?
              (singleActiveMigration.platformType + 'Transition') as Platform : singleActiveMigration.platformType}
      date={migrationPlannedDates} liveDate={liveDates}
    />
    : <PostMigrationNotice platform={singleActiveMigration.platformType} />;
}
